import { type FormEvent, memo, useState } from "react";
import "Components/ConfigLayout/Camera/AiAnalytics/AiAnalytics.less";
import { Card, SettingsLayout, Switch, Tabs } from "@clintonelec/react-storybook";
import { useAppDispatch, useAppSelector } from "Data/Redux/Store";
import { isEqual } from "lodash";
import { selectAiAnalytics, setAiAnalyticsAction } from "Data/Redux/Slices/Settings/Camera/AiAnalytics";
import { produce } from "immer";
import AiRuleSettings from "Components/ConfigLayout/Camera/AiAnalytics/AiRuleSettings";
import AdvancedAiSettings from "Components/ConfigLayout/Camera/AiAnalytics/AdvancedAiSettings";
import { AiRuleType } from "Data/Objects/Camera";
import ActionButtons from "Components/ConfigLayout/ActionButtons";
import AiExclusion from "Components/ConfigLayout/Camera/AiAnalytics/AiExclusion";

function AiAnalytics() {
	const dispatch = useAppDispatch();
	const aiAnalyticsState = useAppSelector(selectAiAnalytics);
	const localAiAnalyticsState = useState(aiAnalyticsState);
	const [ localAiAnalyticsSettings, setLocalAiAnalyticsSettings ] = localAiAnalyticsState;
	const { rules } = localAiAnalyticsSettings;
	const setAiAnalytics = () => dispatch(setAiAnalyticsAction(localAiAnalyticsSettings));
	const firstExistingRuleIndex = aiAnalyticsState.rules.findIndex((rule) => !!rule);
	const [ selectedRuleIndex, setSelectedRuleIndex ] = useState<number>(firstExistingRuleIndex);
	const firstExistingExclusionIndex = aiAnalyticsState.exclusionZones.findIndex((pointsArray) => !!pointsArray);
	const [ activeExclusionIndex, setActiveExclusionIndex ] = useState(firstExistingExclusionIndex);
	let incorrectConfiguration = false;

	if (rules?.[ selectedRuleIndex ]?.type === AiRuleType.ZONE) {
		const { loiter, enter, exit, intrusion } = rules[ selectedRuleIndex ];

		if (!loiter && !enter && !exit && !intrusion) {
			incorrectConfiguration = true;
		}
	}

	if (selectedRuleIndex >= 0 && rules[ selectedRuleIndex ]) {
		const { human, vehicle, bike } = rules[ selectedRuleIndex ].objectTypes;

		if (!human && !vehicle && !bike) {
			incorrectConfiguration = true;
		}
	}

	const handleFormSubmit = (event: FormEvent) => {
		event.preventDefault();

		setAiAnalytics();
	};

	const handleFormReset = (event: FormEvent) => {
		event.preventDefault();

		const currentlySelectedRuleExists = !!aiAnalyticsState.rules[ selectedRuleIndex ];
		const currentlySelectedExclusionExists = !!aiAnalyticsState.exclusionZones[ activeExclusionIndex ];

		if (!currentlySelectedRuleExists) {
			setSelectedRuleIndex(firstExistingRuleIndex);
		}

		if (!currentlySelectedExclusionExists) {
			setActiveExclusionIndex(firstExistingExclusionIndex);
		}

		setLocalAiAnalyticsSettings(aiAnalyticsState);
	};

	const handleEnabledChange = (checked: boolean) => {
		setLocalAiAnalyticsSettings(produce(localAiAnalyticsSettings, (draft) => {
			draft.enabled = checked;
		}));
	};

	const panes = [
		{
			title: "AI Rule Setup",
			content: <AiRuleSettings
				localAiAnalyticsState={ localAiAnalyticsState }
				selectedRuleIndex={ selectedRuleIndex }
				setSelectedRuleIndex={ setSelectedRuleIndex }
			/>
		},
		{
			title: "Exclusion",
			content: <AiExclusion
				activeZoneIndex={ activeExclusionIndex }
				setActiveZoneIndex={ setActiveExclusionIndex }
				localAiAnalyticsSettings={ localAiAnalyticsState[ 0 ] }
				setLocalAiAnalyticsSettings={ localAiAnalyticsState[ 1 ] }
			/>
		},
		{
			title: "Advanced",
			content: <AdvancedAiSettings
				localAiAnalyticsState={ localAiAnalyticsState }
			/>
		}
	];

	return (
		<SettingsLayout.Form
			className="ai-analytics"
			noValidate
			onReset={ handleFormReset }
			onSubmit={ handleFormSubmit }
		>
			<SettingsLayout.ScrollableContainer>
				<Card size="large" title="AI Analytics">
					<div className="enabled-switch">
						<Switch
							checked={ localAiAnalyticsSettings.enabled }
							onChange={ handleEnabledChange }
						/>
						<span>Enable AI Analytics</span>
					</div>
					<Tabs panes={ panes } />
				</Card>
			</SettingsLayout.ScrollableContainer>
			<ActionButtons
				disabled={ isEqual(localAiAnalyticsSettings, aiAnalyticsState) || incorrectConfiguration }
			/>
		</SettingsLayout.Form>
	);
}

export default memo(AiAnalytics);
